import React, { useState } from 'react';

import {
    Box,
    Tabs,
    Tab,
    TabPanel,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Modal, ShowErrors } from 'common/components';
import yupResolver from 'common/utils/yupResolver';

import * as actions from './actions';
import Footer from './components/Footer';
import getConfig from './partners/getConfig';
import * as selectors from './selectors';

const ProductForm = ({
    callback,
    canDelete,
    errors,
    filterData,
    financierId,
    financierType,
    handleClickBulkItems,
    initialValues,
    modalIsOpen,
    save,
    toggleModalProduct,
}) => {
    const { t } = useTranslation();

    const { getTabs, name, validate } = getConfig(financierId);

    const [selectedTab, setSelectedTab] = useState('parameters');
    const { control, getValues, handleSubmit, setValue } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    const [disabled, typeAlgorithm] = useWatch({
        control,
        name: ['disabled', 'type_algorithm'],
    });

    const handleOnClickComplete = (values) =>
        save({ ...values, financier: financierId }, callback);
    const handleChange = (_, newValue) => setSelectedTab(newValue);
    const onExited = () => setSelectedTab('parameters');
    const onHide = () => toggleModalProduct(false);

    const productId = initialValues?.id;
    const isNewRecord = productId ? false : true;
    const title = `${isNewRecord ? t('Add') : t('Edit')} ${t('Product')}`;

    const tabs = getTabs({
        control,
        disabled,
        financierType,
        getValues,
        initialValues,
        isNewRecord,
        setValue,
        typeAlgorithm,
    });

    const enabledTabs = tabs.filter((tab) => tab.visible !== false);

    return (
        <Modal
            onExited={onExited}
            onHide={onHide}
            show={modalIsOpen}
            size="lg"
            title={title}
        >
            <form>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        onChange={handleChange}
                        scrollButtons="auto"
                        value={selectedTab}
                        variant="scrollable"
                    >
                        {enabledTabs.map((tab) => (
                            <Tab
                                id={tab.id}
                                key={tab.id}
                                label={tab.label}
                                value={tab.value}
                            />
                        ))}
                    </Tabs>
                </Box>

                {enabledTabs.map((tab) => (
                    <TabPanel
                        key={tab.id}
                        selectedTab={selectedTab}
                        value={tab.value}
                    >
                        {tab.component}
                    </TabPanel>
                ))}

                {errors && errors.length ? (
                    <ShowErrors errors={errors} />
                ) : null}

                <Footer
                    canDelete={canDelete}
                    control={control}
                    disabled={disabled}
                    filterData={filterData}
                    handleClickBulkItems={handleClickBulkItems}
                    onClick={handleSubmit(handleOnClickComplete)}
                    setValue={setValue}
                />

                <Typography variant="caption">{name}</Typography>
            </form>
        </Modal>
    );
};

ProductForm.propTypes = {
    callback: PropTypes.func,
    canDelete: PropTypes.bool,
    errors: PropTypes.array,
    filterData: PropTypes.object,
    financierId: PropTypes.string,
    financierType: PropTypes.number,
    handleClickBulkItems: PropTypes.func,
    initialValues: PropTypes.object,
    modalIsOpen: PropTypes.bool,
    save: PropTypes.func,
    toggleModalProduct: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getSaveProductErrors,
    initialValues: selectors.getInitialValues,
    modalIsOpen: selectors.getProductModalIsOpen,
});

const mapDispatchToProps = (dispatch) => ({
    save: (values, callback) => dispatch(actions.save(values, callback)),
    toggleModalProduct: (status) =>
        dispatch(actions.toggleModalProduct(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductForm);
